import React, { useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import { useToast } from '../../contexts/ToastContext';
import { useOrgFeatureFlag } from '../../hooks/useOrgFeatureFlags';
import { placeholderApi } from '../../services/placeholderApi';
import { integrationApiService } from '../../services/integrationApi';
import { tokens } from '../../theme/tokens';
import { BrandSelect, EmptyState, InlineMessage, SectionCard, StatusChip } from '../ui';

export interface UnmatchedShopifyListing {
  shopifyProductId: string;
  title: string;
  sku?: string;
  barcode?: string;
  vendor?: string;
}

interface UnmatchedListingPlaceholdersProps {
  integrationId: string;
  listings: UnmatchedShopifyListing[];
}

/**
 * Shopify listings whose SKU or barcode Motzu does not know (Jordan, integration
 * match review). "Create a pricing-only placeholder" makes a product for the
 * listing, adds it to the sync selection and links it to the listing, so a
 * vendor price or rebate can reach it. Nothing about the listing is changed and
 * a listing without a SKU or barcode cannot be matched, so it has no button.
 */
const UnmatchedListingPlaceholders: React.FC<UnmatchedListingPlaceholdersProps> = ({ integrationId, listings }) => {
  const { t } = useAppTranslation();
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const { enabled } = useOrgFeatureFlag('placeholder_link_only');
  const [brands, setBrands] = useState<Record<string, string>>({});
  const [created, setCreated] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState<string | null>(null);

  if (!enabled || listings.length === 0) return null;

  const create = async (listing: UnmatchedShopifyListing) => {
    const brand = (brands[listing.shopifyProductId] ?? listing.vendor ?? '').trim();
    setBusy(listing.shopifyProductId);
    try {
      await placeholderApi.createFromListing({
        integrationId,
        shopifyProductId: listing.shopifyProductId,
        title: listing.title,
        brand,
        sku: listing.sku,
        barcode: listing.barcode,
      });
      setCreated((prev) => new Set(prev).add(listing.shopifyProductId));
      queryClient.invalidateQueries({ queryKey: ['store-placeholders'] });
      queryClient.invalidateQueries({ queryKey: ['store-sync-selection'] });
      showToast(t('store.placeholders.listingCreated'), 'success');
    } catch (error) {
      showToast(error instanceof Error ? error.message : t('store.placeholders.listingError'), 'error');
    } finally {
      setBusy(null);
    }
  };

  return (
    <SectionCard title={t('store.placeholders.listingsTitle', { count: listings.length })} subtitle={t('store.placeholders.listingsSubtitle')} dense padded sx={{ mt: 2 }}>
      <InlineMessage severity="info">{t('store.placeholders.rule')}</InlineMessage>
      <Box sx={{ display: 'flex', flexDirection: 'column', maxHeight: 360, overflow: 'auto' }}>
        {listings.map((listing) => {
          const identifiable = Boolean(listing.sku || listing.barcode);
          const done = created.has(listing.shopifyProductId);
          const brand = brands[listing.shopifyProductId] ?? listing.vendor ?? '';
          return (
            <Box
              key={listing.shopifyProductId}
              sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1, borderTop: `1px solid ${tokens.line.hairline}`, flexWrap: 'wrap' }}
            >
              <Box sx={{ flex: 1, minWidth: 200 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>{listing.title}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {[listing.sku ? `SKU ${listing.sku}` : null, listing.barcode ? `UPC ${listing.barcode}` : null].filter(Boolean).join(' · ') || t('store.placeholders.noIdentifier')}
                </Typography>
              </Box>
              {done ? (
                <StatusChip label={t('store.placeholders.created')} tone="positive" size="small" />
              ) : (
                identifiable && (
                  <>
                    <BrandSelect
                      label={t('store.placeholders.brand')}
                      value={brand}
                      onChange={(next) => setBrands((prev) => ({ ...prev, [listing.shopifyProductId]: next }))}
                      sx={{ width: 200 }}
                    />
                    <Button
                      size="small"
                      variant="outlined"
                      disabled={busy !== null || !brand.trim()}
                      onClick={() => create(listing)}
                    >
                      {t('store.placeholders.createFromListing')}
                    </Button>
                  </>
                )
              )}
            </Box>
          );
        })}
      </Box>
    </SectionCard>
  );
};

/**
 * The Mapping tab's entry to the same list: one read-only scan of the store's
 * Shopify catalog on request (the scan links and changes nothing), then the
 * listings Motzu did not match.
 */
export const UnmatchedListingsScan: React.FC<{ integrationId: string }> = ({ integrationId }) => {
  const { t } = useAppTranslation();
  const { showToast } = useToast();
  const { enabled } = useOrgFeatureFlag('placeholder_link_only');
  const [scanning, setScanning] = useState(false);
  const [listings, setListings] = useState<UnmatchedShopifyListing[] | null>(null);

  if (!enabled) return null;

  const scan = async () => {
    setScanning(true);
    try {
      const result = await integrationApiService.scanProductsAndWait(integrationId);
      setListings(result.unmatchedShopify || []);
    } catch (error) {
      showToast(error instanceof Error ? error.message : t('store.placeholders.scanError'), 'error');
    } finally {
      setScanning(false);
    }
  };

  return (
    <>
      <SectionCard
        title={t('store.placeholders.scanTitle')}
        subtitle={t('store.placeholders.scanSubtitle')}
        actions={
          <Button size="small" variant="outlined" onClick={scan} disabled={scanning}>
            {scanning ? t('store.placeholders.scanning') : t('store.placeholders.scan')}
          </Button>
        }
        dense
        padded
      >
        {listings !== null && listings.length === 0 && (
          <EmptyState size="compact" title={t('store.placeholders.scanNoneTitle')} description={t('store.placeholders.scanNoneDescription')} />
        )}
        {listings === null && <Typography variant="body2" color="text.secondary">{t('store.placeholders.scanIdle')}</Typography>}
      </SectionCard>
      {listings !== null && <UnmatchedListingPlaceholders integrationId={integrationId} listings={listings} />}
    </>
  );
};

export default UnmatchedListingPlaceholders;
